import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  LEGACY_CZ_DATE_FORMAT,
  formatDate,
  formatDayMonthShort,
  formatMonthYear,
  formatWeekdayLong,
  getActiveDateFormat,
} from '../../utils/dateFormat';

export type ViewMode = 'month' | 'week' | 'day';

interface TimesheetNavigationProps {
  year: number;
  month: number; // 0-indexed
  viewMode: ViewMode;
  weekStart?: Date;
  selectedDate?: string; // YYYY-MM-DD
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewModeChange: (mode: ViewMode) => void;
}

export function TimesheetNavigation({
  year,
  month,
  viewMode,
  weekStart,
  selectedDate,
  onPrevious,
  onNext,
  onToday,
  onViewModeChange,
}: TimesheetNavigationProps) {
  // MKS-315 — the period title follows the rest of the /time UI, whose own day
  // names read "Mo Tu We", rather than the Czech "říjen 2026" it used to show.
  const locale = 'en-US';
  // MKS-351's own flag is still live: while it is off the legacy spec is the
  // active one and the title is formatted from `locale` here, not by
  // `utils/dateFormat`. The two are not interchangeable — `formatMonthYear`
  // follows ENGLISH_LABEL_FORMAT, which is MKS-351's spec, not this one.
  const sharedDateFormat = getActiveDateFormat() !== LEGACY_CZ_DATE_FORMAT;

  const getTitle = () => {
    if (viewMode === 'month') {
      const monthStart = new Date(year, month);
      return sharedDateFormat
        ? formatMonthYear(monthStart)
        : monthStart.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    if (viewMode === 'week' && weekStart) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const fmt = (d: Date) =>
        sharedDateFormat
          ? formatDayMonthShort(d)
          : d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      return `${fmt(weekStart)} – ${fmt(weekEnd)}, ${weekEnd.getFullYear()}`;
    }
    if (viewMode === 'day' && selectedDate) {
      if (sharedDateFormat) {
        return `${formatWeekdayLong(selectedDate)}, ${formatDate(selectedDate)}`;
      }
      const d = new Date(selectedDate + 'T00:00:00');
      return d.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    return '';
  };

  const isCurrent = () => {
    const today = new Date();
    if (viewMode === 'month') {
      return today.getFullYear() === year && today.getMonth() === month;
    }
    if (viewMode === 'week' && weekStart) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return today >= weekStart && today <= weekEnd;
    }
    if (viewMode === 'day' && selectedDate) {
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      return todayStr === selectedDate;
    }
    return false;
  };

  const viewModes: { value: ViewMode; label: string }[] = [
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' },
    { value: 'day', label: 'Day' },
  ];

  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevious}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            aria-label="Next"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          {viewModes.map((mode) => (
            <button
              key={mode.value}
              type="button"
              onClick={() => onViewModeChange(mode.value)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === mode.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-900">{getTitle()}</h2>

      <button
        type="button"
        onClick={onToday}
        disabled={isCurrent()}
        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Today
      </button>
    </div>
  );
}
